import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

/**
 * Reusable contextual help dialog for complex modules.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   icon: lucide icon component shown beside title
 *   title: string
 *   intro: React node | string (renders inside the highlighted gradient box)
 *   sections: [{ icon, iconColor, title, body }]  body can be React node or array of strings (rendered as bullet list)
 *   ctaLabel?: string
 *   onCta?: () => void
 *   accentColor?: string (hex for icon background) — default brand orange
 */
const HelpInfoDialog = ({
  open,
  onClose,
  icon: Icon,
  title,
  intro,
  sections = [],
  ctaLabel,
  onCta,
  accentColor = '#FF6B4A',
}) => {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl">
            {Icon && <Icon className="w-6 h-6" style={{ color: accentColor }} />}
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-2 text-sm text-stone-700">
          {intro && (
            <section className="bg-gradient-to-r from-orange-50 to-violet-50/60 border border-orange-100 rounded-2xl p-4 leading-relaxed">
              {intro}
            </section>
          )}

          {sections.map((s, idx) => (
            <section key={idx}>
              <h3 className="font-heading font-bold text-base text-stone-900 mb-2 flex items-center gap-2">
                {s.icon && <s.icon className="w-4 h-4" style={{ color: s.iconColor || accentColor }} />}
                {s.title}
              </h3>
              {Array.isArray(s.body) ? (
                <ul className="space-y-1.5 ml-4 list-disc text-stone-600">
                  {s.body.map((item, i) => (
                    <li key={i}>
                      {typeof item === 'string' ? <span dangerouslySetInnerHTML={{ __html: item }} /> : item}
                    </li>
                  ))}
                </ul>
              ) : typeof s.body === 'string' ? (
                <div className="text-stone-600" dangerouslySetInnerHTML={{ __html: s.body }} />
              ) : (
                <div className="text-stone-600">{s.body}</div>
              )}
            </section>
          ))}

          <div className="flex gap-3 pt-4 border-t border-stone-100">
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">Cerrar</Button>
            {ctaLabel && onCta && (
              <Button onClick={onCta} className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] text-white rounded-full">
                {ctaLabel}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HelpInfoDialog;
